'use client';

import { configKeepData5Mins } from '@/config/useQuery';
import { APP_BUSINESS_DICTIONARY_CODES } from '@/constant/dynamic-form-constants';
import { nameof } from '@/lib/utils';
import { colorPairFromText, ColorPairFromTextResult } from '@/lib/color';
import { ProcedureTypeResponse } from '@/server/api/v1/procedure-type/type';
import {
    isUisStudentProcedureTypeEnabled,
    mergeUisStudentIntoList,
    mergeUisStudentIntoTree,
    parseUisLoaiHinhDaoTao,
} from '@/server/api/v1/procedure-type/uis-student-procedure-type';
import { LogicalOperator } from '@/server/base/base-trpc-service';
import { api } from '@/trpc/client';
import { SortValue } from '@/types/enum';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';


const loopChild = (items: ProcedureTypeResponse[]): (ProcedureTypeResponse & ColorPairFromTextResult)[] => {
    return items.map(({ Childrens, ...x }) => ({
        ...x,
        Childrens: Childrens ? loopChild(Childrens) : [],
        ...colorPairFromText(x.Name),
    })) ?? []
}

const useQueryProcedureTypeTree = () => {
    const { data, isLoading, isFetching } = api.procedureType.GetTree.useQuery({
        numOfParentBefore: 0,
        treeDepth: 3,
        sorts: [
            {
                key: nameof<ProcedureTypeResponse>('Ordinal'),
                value: SortValue.ASC,
            },
            {
                key: nameof<ProcedureTypeResponse>('NumberOfParent'),
                value: SortValue.ASC,
            },
        ],
    }, configKeepData5Mins);

    const options = useMemo(
        () =>
            loopChild(data?.data?.Result ?? []),
        [data]
    );

    return {
        data: options,
        isLoading,
        isFetching,
    };
};

const useQueryProcedureType = () => {
    const { data, isLoading, isFetching } = api.procedureType.GetAll.useQuery({
        sorts: [
            {
                key: nameof<ProcedureTypeResponse>('Ordinal'),
                value: SortValue.ASC,
            },
            {
                key: nameof<ProcedureTypeResponse>('NumberOfParent'),
                value: SortValue.ASC,
            },
        ],
    }, configKeepData5Mins);

    return {
        data: data?.data?.Result ?? [],
        isLoading,
        isFetching,
    };
};

/**
 * Danh sách loại hình đào tạo từ UIS (`UIS_LoaiHinhDaoTao`) — chỉ gọi khi cờ
 * `BUSINESS_CONFIG.student_procedure_type_source = 'uis'`. Lỗi ⇒ mảng rỗng
 * (cây không có nhóm Sinh viên).
 */
const useQueryUisStudentTypes = () => {
    const enabled = isUisStudentProcedureTypeEnabled();
    const transSignIn = useTranslations('SignIn');
    const { data, isLoading, isFetching } =
        api.appBusinessRequirement.Execute.useQuery(
            {
                Value: APP_BUSINESS_DICTIONARY_CODES.uis_loai_hinh_dao_tao,
                Params: {},
            },
            { ...configKeepData5Mins, enabled }
        );

    const studyTypes = useMemo(
        () => (data?.isSuccessStatus ? parseUisLoaiHinhDaoTao(data.data) : []),
        [data]
    );

    return {
        enabled,
        studyTypes,
        studentLabel: transSignIn('Student'),
        isLoading: enabled && isLoading,
        isFetching: enabled && isFetching,
    };
};

const useQueryShowUiProcedureTypeTree = () => {
    const uis = useQueryUisStudentTypes();
    const { data, isLoading, isFetching } = api.procedureType.GetTree.useQuery({
        numOfParentBefore: 0,
        treeDepth: 3,
        filters: [{
            key: nameof<ProcedureTypeResponse>('IsShowUi'),
            values: [true],
            logicalOperator: LogicalOperator.And
        }],
        sorts: [
            {
                key: nameof<ProcedureTypeResponse>('Ordinal'),
                value: SortValue.ASC,
            },
            {
                key: nameof<ProcedureTypeResponse>('NumberOfParent'),
                value: SortValue.ASC,
            },
        ],
    }, configKeepData5Mins);

    const result = useMemo(() => {
        const tree = data?.data?.Result ?? [];
        return uis.enabled
            ? mergeUisStudentIntoTree(tree, uis.studyTypes, uis.studentLabel)
            : tree;
    }, [data, uis.enabled, uis.studyTypes, uis.studentLabel]);

    return {
        data: result,
        isLoading: isLoading || uis.isLoading,
        isFetching: isFetching || uis.isFetching,
    };
};

const useQueryShowUiProcedureType = () => {
    const uis = useQueryUisStudentTypes();
    const { data, isLoading, isFetching } = api.procedureType.GetAll.useQuery({
        filters: [{
            key: nameof<ProcedureTypeResponse>('IsShowUi'),
            values: [true],
            logicalOperator: LogicalOperator.And
        }],
        sorts: [
            {
                key: nameof<ProcedureTypeResponse>('Ordinal'),
                value: SortValue.ASC,
            },
            {
                key: nameof<ProcedureTypeResponse>('NumberOfParent'),
                value: SortValue.ASC,
            },
        ],
    }, configKeepData5Mins);

    const options = useMemo(() => {
        const list = data?.data?.Result ?? [];
        return loopChild(
            uis.enabled
                ? mergeUisStudentIntoList(list, uis.studyTypes, uis.studentLabel)
                : list
        );
    }, [data, uis.enabled, uis.studyTypes, uis.studentLabel]);

    return {
        data: options,
        isLoading: isLoading || uis.isLoading,
        isFetching: isFetching || uis.isFetching,
    };
};


export { useQueryProcedureType, useQueryShowUiProcedureType, useQueryProcedureTypeTree, useQueryShowUiProcedureTypeTree }